<template>
  <div v-if="show" class="modal fade show d-block" tabindex="-1" role="dialog" aria-labelledby="liquorSignatoryModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="liquorSignatoryModalLabel">
            <i class="mdi mdi-draw me-2"></i>Liquor Licence Signatory Setup - {{selectedUser.names}}
          </h5>
          <button type="button" class="btn-close" @click="$emit('close')" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <!-- User Information -->
          <div class="row mb-4">
            <div class="col-md-12">
              <div class="alert alert-info" role="alert">
                <i class="mdi mdi-information me-2"></i>
                <strong>Setting up signatory roles for:</strong> {{selectedUser.names}} ({{selectedUser.category}}) - {{selectedUser.subCountyName}}
              </div>
            </div>
          </div>

          <!-- Signatory Roles Cards -->
          <div class="row">
            <!-- Issuing Officer Card -->
            <div class="col-md-4 mb-3">
              <div class="card signatory-card">
                <div class="card-header bg-primary text-white">
                  <h6 class="mb-0" style="color: white">
                    <i class="mdi mdi-account-edit me-2"></i>Issuing Officer
                  </h6>
                  <small>SCADRC (Revenue Officer)</small>
                </div>
                <div class="card-body">
                  <div class="signatory-info mb-3">
                    <p class="mb-2"><strong>Role:</strong> Issues the liquor licence</p>
                    <p class="mb-2"><strong>Requirement:</strong> Must be the person who invoiced</p>
                    <p class="mb-0"><strong>Source:</strong> Auto-detected from invoice details</p>
                  </div>

                  <div class="alert alert-warning" role="alert">
                    <i class="mdi mdi-lightbulb me-2"></i>
                    <small><strong>Note:</strong> This role is automatically assigned to the user who creates the invoice. No manual setup required.</small>
                  </div>

                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" disabled checked>
                    <label class="form-check-label text-muted">
                      <small>Auto-assigned based on invoice</small>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Chairperson Card -->
            <div class="col-md-4 mb-3">
              <div class="card signatory-card" :class="{'border-danger': !userHasSignature && signatorRoles.isChairperson}">
                <div class="card-header bg-success text-white">
                  <h6 class="mb-0" style="color: white">
                    <i class="mdi mdi-account-star me-2"></i>Chairperson
                  </h6>
                  <small>SCADRC - Sub County Administrator</small>
                </div>
                <div class="card-body">
                  <div class="signatory-info mb-3">
                    <p class="mb-2"><strong>Role:</strong> Chairperson of Sub County Alcoholic Drinks Control Committee</p>
                    <p class="mb-2"><strong>Authority:</strong> Sub County Administrator</p>
                    <p class="mb-0"><strong>Scope:</strong> {{selectedUser.subCountyName}} Sub County</p>
                  </div>

                  <div class="current-status mb-3">
                    <div v-if="isCurrentChairperson" class="alert alert-success" role="alert">
                      <i class="mdi mdi-check-circle me-2"></i>
                      <strong>Currently assigned as Chairperson</strong>
                    </div>
                    <div v-else class="alert alert-secondary" role="alert">
                      <i class="mdi mdi-information me-2"></i>
                      Not currently assigned as Chairperson
                    </div>
                  </div>

                  <!-- Signature Requirement Alert -->
                  <div v-if="!userHasSignature" class="alert alert-danger" role="alert">
                    <i class="mdi mdi-alert-circle me-2"></i>
                    <small><strong>Signature Required:</strong> This user must upload a signature before being assigned as Chairperson.</small>
                    <div class="mt-2">
                      <button class="btn btn-sm btn-outline-primary" @click="openSignatureModal(selectedUser)">
                        <i class="mdi mdi-upload me-1"></i>Upload Signature
                      </button>
                    </div>
                  </div>

                  <div class="form-check">
                    <input class="form-check-input"
                           type="checkbox"
                           id="chairpersonCheck"
                           v-model="signatorRoles.isChairperson"
                           @change="onRoleChange"
                           :disabled="!userHasSignature">
                    <label class="form-check-label" for="chairpersonCheck" :class="{'text-muted': !userHasSignature}">
                      Assign as Chairperson SCADRC
                      <span v-if="!userHasSignature" class="text-danger ms-1">*</span>
                    </label>
                  </div>

                  <div v-if="signatorRoles.isChairperson" class="mt-2">
                    <small class="text-success">
                      <i class="mdi mdi-check me-1"></i>Will appear on all liquor licences for {{selectedUser.subCountyName}}
                    </small>
                  </div>
                </div>
              </div>
            </div>

            <!-- Secretary Card -->
            <div class="col-md-4 mb-3">
              <div class="card signatory-card" :class="{'border-danger': !userHasSignature && signatorRoles.isSecretary}">
                <div class="card-header bg-info text-white">
                  <h6 class="mb-0" style="color: white">
                    <i class="mdi mdi-account-details me-2"></i>Secretary
                  </h6>
                  <small>SCADRC (Sub County Trade Officer)</small>
                </div>
                <div class="card-body">
                  <div class="signatory-info mb-3">
                    <p class="mb-2"><strong>Role:</strong> Secretary of Sub County Alcoholic Drinks Control Committee</p>
                    <p class="mb-2"><strong>Authority:</strong> Sub County Trade Officer</p>
                    <p class="mb-0"><strong>Scope:</strong> {{selectedUser.subCountyName}} Sub County</p>
                  </div>

                  <div class="current-status mb-3">
                    <div v-if="isCurrentSecretary" class="alert alert-success" role="alert">
                      <i class="mdi mdi-check-circle me-2"></i>
                      <strong>Currently assigned as Secretary</strong>
                    </div>
                    <div v-else class="alert alert-secondary" role="alert">
                      <i class="mdi mdi-information me-2"></i>
                      Not currently assigned as Secretary
                    </div>
                  </div>

                  <!-- Signature Requirement Alert -->
                  <div v-if="!userHasSignature" class="alert alert-danger" role="alert">
                    <i class="mdi mdi-alert-circle me-2"></i>
                    <small><strong>Signature Required:</strong> This user must upload a signature before being assigned as Secretary.</small>
                    <div class="mt-2">
                      <button class="btn btn-sm btn-outline-primary" @click="openSignatureModal(selectedUser)">
                        <i class="mdi mdi-upload me-1"></i>Upload Signature
                      </button>
                    </div>
                  </div>

                  <div class="form-check">
                    <input class="form-check-input"
                           type="checkbox"
                           id="secretaryCheck"
                           v-model="signatorRoles.isSecretary"
                           @change="onRoleChange"
                           :disabled="!userHasSignature">
                    <label class="form-check-label" for="secretaryCheck" :class="{'text-muted': !userHasSignature}">
                      Assign as Secretary SCADRC
                      <span v-if="!userHasSignature" class="text-danger ms-1">*</span>
                    </label>
                  </div>

                  <div v-if="signatorRoles.isSecretary" class="mt-2">
                    <small class="text-success">
                      <i class="mdi mdi-check me-1"></i>Will appear on all liquor licences for {{selectedUser.subCountyName}}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Current Signatory Summary -->
          <div class="row mt-4">
            <div class="col-12">
              <div class="card">
                <div class="card-header">
                  <h6 class="mb-0">
                    <i class="mdi mdi-format-list-bulleted me-2"></i>Current Liquor Signatory Summary - {{selectedUser.subCountyName}}
                  </h6>
                </div>
                <div class="card-body">
                  <div class="table-responsive">
                    <table class="table table-sm">
                      <thead>
                      <tr>
                        <th>Signatory Role</th>
                        <th>Current Assignee</th>
                        <th>Signature Status</th>
                        <th>Actions</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr>
                        <td>
                          <strong>Issuing Officer</strong><br>
                          <small class="text-muted">SCADRC (Revenue Officer)</small>
                        </td>
                        <td>
                          <span class="text-info">Auto-assigned from invoice</span><br>
                          <small class="text-muted">Based on user who creates invoice</small>
                        </td>
                        <td>
                            <span class="badge badge-soft-info">
                              <i class="mdi mdi-cogs me-1"></i>Automatic
                            </span>
                        </td>
                        <td>
                          <small class="text-muted">No action needed</small>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Chairperson</strong><br>
                          <small class="text-muted">SCADRC - Sub County Administrator</small>
                        </td>
                        <td>
                            <span v-if="currentChairperson">
                              {{currentChairperson.names}}<br>
                              <small class="text-muted">{{currentChairperson.category}}</small>
                            </span>
                          <span v-else class="text-warning">Not assigned</span>
                        </td>
                        <td>
                            <span v-if="currentChairperson && currentChairperson.signature" class="badge badge-soft-success">
                              <i class="mdi mdi-check me-1"></i>Has Signature
                            </span>
                          <span v-else class="badge badge-soft-warning">
                              <i class="mdi mdi-alert me-1"></i>No Signature
                            </span>
                        </td>
                        <td>
                          <button v-if="currentChairperson && !currentChairperson.signature"
                                  class="btn btn-sm btn-outline-primary"
                                  @click="openSignatureModal(currentChairperson)">
                            <i class="mdi mdi-upload me-1"></i>Upload
                          </button>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Secretary</strong><br>
                          <small class="text-muted">SCADRC (Sub County Trade Officer)</small>
                        </td>
                        <td>
                            <span v-if="currentSecretary">
                              {{currentSecretary.names}}<br>
                              <small class="text-muted">{{currentSecretary.category}}</small>
                            </span>
                          <span v-else class="text-warning">Not assigned</span>
                        </td>
                        <td>
                            <span v-if="currentSecretary && currentSecretary.signature" class="badge badge-soft-success">
                              <i class="mdi mdi-check me-1"></i>Has Signature
                            </span>
                          <span v-else class="badge badge-soft-warning">
                              <i class="mdi mdi-alert me-1"></i>No Signature
                            </span>
                        </td>
                        <td>
                          <button v-if="currentSecretary && !currentSecretary.signature"
                                  class="btn btn-sm btn-outline-primary"
                                  @click="openSignatureModal(currentSecretary)">
                            <i class="mdi mdi-upload me-1"></i>Upload
                          </button>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Validation Warnings -->
          <div v-if="validationWarnings.length > 0" class="row mt-3">
            <div class="col-12">
              <div class="alert alert-warning" role="alert">
                <h6><i class="mdi mdi-alert me-2"></i>Validation Warnings:</h6>
                <ul class="mb-0">
                  <li v-for="warning in validationWarnings" :key="warning">{{warning}}</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Success/Error Messages -->
          <div v-if="message" class="row mt-3">
            <div class="col-12">
              <div :class="messageType === 'success' ? 'alert alert-success' : 'alert alert-danger'" role="alert">
                <i :class="messageType === 'success' ? 'mdi mdi-check-circle me-2' : 'mdi mdi-alert-circle me-2'"></i>
                {{message}}
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">
            <i class="mdi mdi-close me-2"></i>Cancel
          </button>
          <button type="button"
                  class="btn btn-primary"
                  @click="saveSignatoryRoles"
                  :disabled="saving || !hasChanges || hasBlockingValidations">
            <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
            <i v-else class="mdi mdi-content-save me-2"></i>
            {{saving ? 'Saving...' : 'Save Signatory Roles'}}
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="show" class="modal-backdrop fade show"></div>
</template>

<script>

/* eslint-disable */
export default {
  name: "LiquorSignatoryModal",
  props: {
    show: {
      type: Boolean,
      default: false
    },
    selectedUser: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      saving: false,
      message: null,
      messageType: 'success',
      signatorRoles: {
        isChairperson: false,
        isSecretary: false
      },
      currentChairperson: null,
      currentSecretary: null,
      validationWarnings: [],
      modalInitialized: false
    }
  },
  computed: {
    hasChanges() {
      return this.signatorRoles.isChairperson !== (this.selectedUser.isChairperson || false) ||
          this.signatorRoles.isSecretary !== (this.selectedUser.isSecretary || false);
    },
    hasBlockingValidations() {
      return this.validationWarnings.some(warning =>
          warning.includes('cannot be both') ||
          warning.includes('requires a signature') ||
          warning.includes('must upload a signature')
      );
    },
    isCurrentChairperson() {
      // Check if the selected user is actually the current chairperson
      return this.currentChairperson && this.currentChairperson.id === this.selectedUser.id;
    },
    isCurrentSecretary() {
      // Check if the selected user is actually the current secretary
      return this.currentSecretary && this.currentSecretary.id === this.selectedUser.id;
    },
    userHasSignature() {
      // Check if the selected user has a signature uploaded
      return this.selectedUser.signature && this.selectedUser.signature.trim() !== '';
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.modalInitialized = false;
        this.validationWarnings = [];
        this.message = null;
        this.loadCurrentRoles();
        this.loadCurrentSignatories();
      } else {
        this.resetModal();
      }
    }
  },
  methods: {
    loadCurrentRoles() {
      // Initialize roles based on current user status - ensure boolean conversion
      this.signatorRoles = {
        isChairperson: this.selectedUser.isChairperson === true || this.selectedUser.isChairperson === "true",
        isSecretary: this.selectedUser.isSecretary === true || this.selectedUser.isSecretary === "true"
      };

      // Debug logging to see what's happening
      console.log('Loading roles for:', this.selectedUser.names);
      console.log('User isChairperson:', this.selectedUser.isChairperson);
      console.log('User isSecretary:', this.selectedUser.isSecretary);
      console.log('User signature:', this.selectedUser.signature);
      console.log('User has signature:', this.userHasSignature);
      console.log('Initialized signatorRoles:', this.signatorRoles);

      // Mark modal as initialized after roles are loaded
      this.$nextTick(() => {
        this.modalInitialized = true;
        // Run initial validation to check signature requirements
        this.validateRoles();
      });
    },

    loadCurrentSignatories() {
      // Load current signatories for this sub county
      this.$emit('load-signatories', this.selectedUser.subCountyID);
    },

    updateCurrentSignatories(signatories) {
      // Method to be called by parent to update current signatories
      this.currentChairperson = signatories.chairperson;
      this.currentSecretary = signatories.secretary;
    },

    onRoleChange() {
      // Only validate if modal is fully initialized
      if (this.modalInitialized) {
        this.validateRoles();
      }
    },

    validateRoles() {
      this.validationWarnings = [];

      // CRITICAL: Check signature requirements FIRST (blocking validation)
      if (!this.userHasSignature) {
        if (this.signatorRoles.isChairperson) {
          this.validationWarnings.push("User must upload a signature before being assigned as Chairperson.");
        }
        if (this.signatorRoles.isSecretary) {
          this.validationWarnings.push("User must upload a signature before being assigned as Secretary.");
        }
      }

      // Only show this warning if we're trying to assign BOTH roles to the SAME user
      if (this.signatorRoles.isChairperson && this.signatorRoles.isSecretary) {
        this.validationWarnings.push("A user cannot be both Chairperson and Secretary simultaneously.");
      }

      // Check if removing current roles (only if user currently has the role)
      if (this.isCurrentChairperson && !this.signatorRoles.isChairperson) {
        this.validationWarnings.push("Removing Chairperson role will affect all existing liquor licences in this sub county.");
      }

      if (this.isCurrentSecretary && !this.signatorRoles.isSecretary) {
        this.validationWarnings.push("Removing Secretary role will affect all existing liquor licences in this sub county.");
      }

      // Check for conflicts with existing assignees (informational warnings)
      if (this.signatorRoles.isChairperson &&
          this.currentChairperson &&
          this.currentChairperson.id !== this.selectedUser.id) {
        this.validationWarnings.push(`Note: Chairperson role is currently assigned to ${this.currentChairperson.names}. This will reassign the role.`);
      }

      if (this.signatorRoles.isSecretary &&
          this.currentSecretary &&
          this.currentSecretary.id !== this.selectedUser.id) {
        this.validationWarnings.push(`Note: Secretary role is currently assigned to ${this.currentSecretary.names}. This will reassign the role.`);
      }
    },

    openSignatureModal(user) {
      this.$emit('open-signature-modal', user);
    },

    async saveSignatoryRoles() {
      if (!this.hasChanges) {
        this.message = "No changes to save.";
        this.messageType = 'info';
        return;
      }

      // Validate before saving
      if (this.hasBlockingValidations) {
        this.message = "Please resolve validation warnings before saving.";
        this.messageType = 'error';
        return;
      }

      this.saving = true;
      this.message = null;

      try {
        const roleData = {
          userId: this.selectedUser.id,
          idNo: this.selectedUser.idNo,
          names: this.selectedUser.names,
          subCountyID: this.selectedUser.subCountyID,
          subCountyName: this.selectedUser.subCountyName,
          isChairperson: this.signatorRoles.isChairperson,
          isSecretary: this.signatorRoles.isSecretary
        };

        this.$emit('save-signatory-roles', roleData);

      } catch (error) {
        this.message = "Error saving signatory roles: " + error.message;
        this.messageType = 'error';
        this.saving = false;
      }
    },

    handleSaveSuccess(message) {
      this.saving = false;
      this.message = message;
      this.messageType = 'success';

      // Update selected user roles
      this.selectedUser.isChairperson = this.signatorRoles.isChairperson;
      this.selectedUser.isSecretary = this.signatorRoles.isSecretary;

      // Auto-close after 2 seconds
      setTimeout(() => {
        this.$emit('close');
      }, 2000);
    },

    handleSaveError(error) {
      this.saving = false;
      this.message = error;
      this.messageType = 'error';
    },

    resetModal() {
      this.saving = false;
      this.message = null;
      this.messageType = 'success';
      this.validationWarnings = [];
      this.currentChairperson = null;
      this.currentSecretary = null;
      this.modalInitialized = false;
      // Explicitly reset to false
      this.signatorRoles = {
        isChairperson: false,
        isSecretary: false
      };
      console.log('Modal reset - signatorRoles:', this.signatorRoles);
    },

    // Add method to manually clear roles for debugging
    clearRoles() {
      this.signatorRoles.isChairperson = false;
      this.signatorRoles.isSecretary = false;
      this.validationWarnings = [];
      console.log('Roles manually cleared:', this.signatorRoles);
    }
  }
}
</script>

<style scoped>
.signatory-card {
  height: 100%;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.signatory-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.signatory-card.border-danger {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 0.1rem rgba(220, 53, 69, 0.1);
}

.signatory-card .card-header {
  font-weight: 600;
}

.signatory-info p {
  font-size: 0.9rem;
  line-height: 1.4;
}

.form-check-input:checked {
  background-color: #28a745;
  border-color: #28a745;
}

.form-check-input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.form-check-label.text-muted {
  opacity: 0.6;
  cursor: not-allowed;
}

.current-status .alert {
  padding: 8px 12px;
  margin-bottom: 0;
  font-size: 0.85rem;
}

.table th {
  background-color: #f8f9fa;
  font-weight: 600;
  border-top: none;
  font-size: 0.9rem;
}

.table td {
  font-size: 0.9rem;
  vertical-align: middle;
}

.badge {
  font-size: 0.8rem;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

/* Validation warnings styling */
.alert-warning ul {
  padding-left: 20px;
  margin-bottom: 0;
}

.alert-warning li {
  margin-bottom: 5px;
}

/* Modal sizing for larger content */
.modal-xl {
  max-width: 1200px;
}

@media (max-width: 768px) {
  .modal-xl {
    max-width: 95%;
  }

  .signatory-card {
    margin-bottom: 15px;
  }
}

/* Button styling */
.btn-primary {
  background-color: #007bff;
  border-color: #007bff;
}

.btn-primary:hover {
  background-color: #0056b3;
  border-color: #004085;
}

.btn-primary:disabled {
  background-color: #6c757d;
  border-color: #6c757d;
  opacity: 0.65;
}

.btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
}

.btn-outline-primary {
  color: #007bff;
  border-color: #007bff;
}

.btn-outline-primary:hover {
  color: #fff;
  background-color: #007bff;
  border-color: #007bff;
}

/* Enhanced modal styling */
.modal-content {
  border-radius: 12px;
  border: none;
  box-shadow: 0 20px 25px rgba(0, 0, 0, 0.1);
}

.modal-header {
  background-color: #f8fafc;
  color: #374151;
  border-radius: 12px 12px 0 0;
  border-bottom: 1px solid #e5e7eb;
}

.modal-backdrop {
  background-color: rgba(0, 0, 0, 0.5);
}

/* Form styling */
.form-check-input {
  border-radius: 4px;
  margin-top: 0.25rem;
}

.form-check-label {
  font-weight: 500;
  color: #374151;
}

/* Alert styling */
.alert {
  border-radius: 8px;
  border: none;
}

.alert-info {
  background-color: rgba(59, 130, 246, 0.1);
  color: #1e40af;
}

.alert-warning {
  background-color: rgba(245, 158, 11, 0.1);
  color: #92400e;
}

.alert-success {
  background-color: rgba(34, 197, 94, 0.1);
  color: #166534;
}

.alert-secondary {
  background-color: rgba(107, 114, 128, 0.1);
  color: #374151;
}

.alert-danger {
  background-color: rgba(239, 68, 68, 0.1);
  color: #991b1b;
}

/* Badge styling */
.badge-soft-info {
  background-color: rgba(59, 130, 246, 0.1);
  color: #1e40af;
}

.badge-soft-success {
  background-color: rgba(34, 197, 94, 0.1);
  color: #166534;
}

.badge-soft-warning {
  background-color: rgba(245, 158, 11, 0.1);
  color: #92400e;
}
</style>